import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CheckCircle, Sparkles, Mail, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import * as z from "zod";
import { trackConversion } from "@/lib/utils";

interface MailchimpExitPopupProps {
  alwaysShow?: boolean;
  treatment?: string;
}

const mailchimpSchema = z.object({
  email: z.string().email("Invalid email address"),
});

export default function MailchimpExitPopup({
  alwaysShow = false,
  treatment,
}: MailchimpExitPopupProps) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Determine treatment based on URL if not passed as prop
  const getTreatmentFromUrl = () => {
    if (treatment) return treatment;

    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (path.includes("neuropathy")) return "neuropathy";
    if (path.includes("erectile-dysfunction")) return "erectile-dysfunction";
    if (path.includes("soft-tissue")) return "soft-tissue";
    if (path.includes("aesthetics")) return "aesthetics";
    return "general";
  };

  useEffect(() => {
    // Check if user already subscribed
    const hasSubscribed =
      localStorage.getItem("mailchimp_subscribed") === "true";
    if (hasSubscribed) return;

    // Check if we should show the popup
    const shouldShowPopup = () => {
      // If alwaysShow is true, always show the popup (for development)
      if (alwaysShow) return true;

      const lastShownAt = localStorage.getItem("mailchimpPopupLastShown");

      // If never shown before, show it
      if (!lastShownAt) return true;

      // Check if it was shown today
      const lastShownDate = new Date(lastShownAt).toDateString();
      const todayDate = new Date().toDateString();

      // Don't show if it was already shown today
      if (lastShownDate === todayDate) return false;

      return true;
    };

    // Setup exit intent detection
    const handleExitIntent = (e: MouseEvent) => {
      // Detect if mouse is leaving through the top of the page
      if (e.clientY <= 0 && shouldShowPopup()) {
        setOpen(true);
        // Update last shown timestamp
        localStorage.setItem(
          "mailchimpPopupLastShown",
          new Date().toISOString(),
        );
      }
    };

    // Add exit intent listener
    document.addEventListener("mouseleave", handleExitIntent);

    // For development, immediately show if alwaysShow is true
    if (alwaysShow) {
      setOpen(true);
    }

    // Clean up
    return () => {
      document.removeEventListener("mouseleave", handleExitIntent);
    };
  }, [alwaysShow]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors({});
    }
  };

  const validateForm = () => {
    try {
      mailchimpSchema.parse({ email });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: { [key: string]: string } = {};
        error.errors.forEach((err) => {
          const path = err.path[0] as string;
          newErrors[path] = err.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);
    const isValid = validateForm();

    if (isValid) {
      setSubmitting(true);
      try {
        const treatmentValue = getTreatmentFromUrl();
        const formBody = new FormData();
        formBody.append("EMAIL", email);
        formBody.append("FNAME", ""); // Empty but kept for backwards compatibility
        formBody.append("TREATMENT", treatmentValue);

        await fetch(
          "https://multnomahmedical.us14.list-manage.com/subscribe/post?u=313545590a6cdc0893b8d4793&id=5c77dc0dca",
          {
            method: "POST",
            body: formBody,
            mode: "no-cors",
          },
        );

        setSubmitted(true);
        localStorage.setItem("mailchimp_subscribed", "true");

        trackConversion(
          "mailchimp_signup",
          50,
          {
            form_type: "exit_popup",
            treatment_interest: treatmentValue,
          },
          { email },
        );

        // Close popup after 2 seconds
        setTimeout(() => {
          setOpen(false);
        }, 2000);
      } catch (error) {
        console.error("Error submitting form:", error);
        setApiError(
          error instanceof Error ? error.message : "An unknown error occurred.",
        );
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden border-green-500 border-2">
        <div className="bg-green-500 text-white p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold">
                Free Shockwave Therapy Guide!
              </h2>
            </div>
          </div>
        </div>

        <div className="px-4 sm:px-6 mb-2 pb-2">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-2">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Join our wellness community and receive your free treatment
                  guide instantly.
                </p>

                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={email}
                    onChange={handleChange}
                    className={`pl-10 h-12 bg-input border-border focus:ring-primary/20 w-full ${errors.email ? "border-red-500" : ""}`}
                    placeholder="Enter your email address"
                    required
                    autoFocus
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200"
                disabled={submitting}
              >
                {submitting ? "Processing..." : "Get Your Free Ebook"}
                <Sparkles className="w-4 h-4 ml-2" />
              </Button>

              {apiError && (
                <p className="text-center text-sm text-red-500">{apiError}</p>
              )}
            </form>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-900">Success!</p>
                <p className="text-sm text-green-700">
                  Check your email for your free ebook.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
